import { useState, type FormEvent } from "react";
import Field from "@/components/base/Field";
import Honeypot from "@/components/base/Honeypot";
import FormStatus from "@/components/base/FormStatus";
import ActionButton from "@/components/base/ActionButton";
import { useEnquirySubmit } from "@/hooks/useEnquirySubmit";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahppnrp14a1h3mr6q8g";
const HONEYPOT = "phone_alt";

export default function ContactEnquiryForm() {
  const { status, message, submit, reset } = useEnquirySubmit();
  const [referenceId, setReferenceId] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const reference = `KLZ-CN-${Date.now().toString(36).toUpperCase().slice(-5)}`;
    setReferenceId(reference);
    submit(event.currentTarget, SUBMIT_ADDR, HONEYPOT);
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <FormStatus status={status} message={message} referenceId={referenceId} />
        <button
          type="button"
          onClick={() => {
            reset();
            setReferenceId("");
          }}
          className="mt-6 inline-flex cursor-pointer items-center gap-2 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <i className="ri-refresh-line" /> Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-enquiry-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <Honeypot name={HONEYPOT} />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Full Name" name="name" required placeholder="Your full name" autoComplete="name" />
        <Field label="Company" name="company" placeholder="Company / organisation" autoComplete="organization" />
        <Field label="Mobile Number" name="mobile" variant="tel" required placeholder="10-digit mobile number" autoComplete="tel" />
        <Field label="Email" name="email" variant="email" required placeholder="you@example.com" autoComplete="email" />
        <Field label="City" name="city" required placeholder="Your city" autoComplete="address-level2" />
        <Field
          label="Enquiry Type"
          name="enquiry_type"
          variant="select"
          required
          options={[
            { value: "general", label: "General Enquiry" },
            { value: "retrofit", label: "Retrofit Enquiry" },
            { value: "fleet", label: "Fleet Enquiry" },
            { value: "dealer", label: "Dealer / Workshop Enquiry" },
            { value: "partnership", label: "Technology Partnership" },
          ]}
        />
        <Field
          label="Message"
          name="message"
          variant="textarea"
          required
          rows={5}
          maxLength={500}
          placeholder="How can we help you? (max 500 characters)"
          className="md:col-span-2"
        />
      </div>

      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <ActionButton type="submit" variant="primary" size="lg" icon="ri-send-plane-line" disabled={status === "submitting"}>
          {status === "submitting" ? "Sending…" : "Send Enquiry"}
        </ActionButton>
        <p className="text-[11px] leading-relaxed text-foreground-500">
          We route your message to the right team based on the enquiry type.
        </p>
      </div>

      <FormStatus status={status} message={message} />
    </form>
  );
}